import { queryOptions, useQuery } from "@tanstack/react-query"
import { cfApi } from "~/store/server/cf/client"

// ---------------------------------------------------------------------------
// Credit Profile — the borrower's COMPLETED soft credit pulls, grouped by deal
// (`GET /api/contacts/credit-profile`). Read-only.
//
// Hand-authored mirror of `CreditProfileSchema` in `@backflip/api-types`
// (web-app deliberately does not depend on that package). The contract carries
// NO FICO number — do not re-add it. A change must land in BOTH places.
// ---------------------------------------------------------------------------

/** Which of a deal's up-to-three credit slots a report came from. */
export type CreditProfileSlot = 1 | 2 | 3

/** One completed pull on a deal credit slot: who was pulled, and how fresh. */
export interface CreditProfileReport {
  slot: CreditProfileSlot
  name: string
  /** ISO date of the pull; null when the pull completed but carries no date. */
  ficoDate: string | null
  /** ISO date the pull stops counting (ficoDate + validityDays); null if unknown. */
  validUntil: string | null
  /** Days left until `validUntil`; negative means expired, null if unknown. */
  daysRemaining: number | null
  expired: boolean
}

/** A deal with at least one completed pull. */
export interface CreditProfileDeal {
  dealId: string
  dealName: string | null
  address: string | null
  reports: CreditProfileReport[]
}

export interface CreditProfileResponse {
  /**
   * True when a read failed or the deal set was truncated — `deals` may be
   * incomplete, or empty despite the borrower having pulls. The ONLY thing
   * separating "we couldn't load" from "you have none"; render them differently.
   */
  degraded: boolean
  deals: CreditProfileDeal[]
  /** Days a pull stays valid. Optional: an older server omits it. */
  validityDays?: number
}

/**
 * Root of every credit-profile query key. `store/queryClient.ts` excludes this
 * prefix from localStorage persistence — build future parameterized keys from
 * it (e.g. `[...prefix, dealId]`) so they stay excluded too.
 */
const CREDIT_PROFILE_KEY_PREFIX = ["cf", "credit-profile"] as const

export const creditProfileQueryKeys = {
  prefix: CREDIT_PROFILE_KEY_PREFIX,
  creditProfile: () => CREDIT_PROFILE_KEY_PREFIX,
} as const

export function creditProfileQueryOptions() {
  return queryOptions({
    queryKey: creditProfileQueryKeys.creditProfile(),
    queryFn: async () =>
      await cfApi.get<CreditProfileResponse>("/api/contacts/credit-profile"),
  })
}

/**
 * Completed credit checks for the logged-in borrower, grouped by deal.
 *
 * Deal order is the server's — the endpoint decides inclusion and ordering, so
 * callers must render `deals` as-is rather than re-sorting.
 */
export const useCreditProfile = () => {
  return useQuery(creditProfileQueryOptions())
}
